import { useMemo } from 'react'

import { useDynamicLeverage as useDynamicLeverageApi } from '@pages/Trade/Markets/hooks/useDynamicLeverage'

import type { AssetClassMarginRequirementDto } from '@model/DxTrade.types'
import type { DynamicLeverage, DynamicLeverageTier } from '@model/DynamicLeverage'

const resolveDynamicLeverage = (
  allEntries: AssetClassMarginRequirementDto[] | undefined,
  instrumentSymbol: string | undefined
): DynamicLeverage | undefined => {
  if (!allEntries?.length || !instrumentSymbol) {
    return undefined
  }

  const bySymbol = allEntries.find(
    (e) => e.instrument?.toLowerCase() === instrumentSymbol.toLowerCase()
  )
  return bySymbol ? toDynamicLeverage(bySymbol) : undefined
}

const toDynamicLeverage = (dto: AssetClassMarginRequirementDto): DynamicLeverage => ({
  id: dto.id,
  instrument: dto.instrument ?? '',
  assetClass: dto.assetClass ?? '',
  tiers: (dto.tiers ?? []).map<DynamicLeverageTier>((t) => ({
    id: t.id,
    marginRequirement: t.marginRequirement,
    maximumLeverage: t.maximumLeverage,
    lotSizeMinimum: t.lotSizeMinimum,
    lotSizeMaximum: t.lotSizeMaximum ?? Number.MAX_SAFE_INTEGER,
  })),
})

const computeMarginFactorForLots = (
  sorted: DynamicLeverageTier[],
  lots: number,
  contractSize: number
): number => {
  if (lots <= 0) return 0
  let total = 0
  for (let index = 0; index < sorted.length; index += 1) {
    const tier = sorted[index]
    const prevTier = index > 0 ? sorted[index - 1] : undefined

    const tierStart = prevTier ? prevTier.lotSizeMaximum : tier.lotSizeMinimum

    if (lots <= tierStart) break

    const lotsInTier = Math.min(lots, tier.lotSizeMaximum) - tierStart
    if (lotsInTier <= 0) continue
    total += lotsInTier * contractSize * (tier.marginRequirement / 100)
  }
  return total
}

export const calculateMarginFactor = (
  tiers: DynamicLeverageTier[],
  orderExposureDeltaLots: number,
  contractSize: number,
  existingNetLots = 0
): number => {
  if (orderExposureDeltaLots === 0 || contractSize <= 0) return 0

  const sorted = [...tiers].sort((a, b) => a.lotSizeMinimum - b.lotSizeMinimum)

  const newNetLots = existingNetLots + orderExposureDeltaLots
  const newFactor = computeMarginFactorForLots(sorted, Math.abs(newNetLots), contractSize)
  const existingFactor = computeMarginFactorForLots(sorted, Math.abs(existingNetLots), contractSize)

  return newFactor - existingFactor
}

export const useDynamicLeverage = (
  instrumentSymbol?: string
): {
  dynamicLeverage?: DynamicLeverage
  isLoading: boolean
} => {
  const { data: allEntries, isLoading } = useDynamicLeverageApi()

  const entries = allEntries

  const dynamicLeverage = useMemo(
    () => resolveDynamicLeverage(entries, instrumentSymbol),
    [entries, instrumentSymbol]
  )

  return { dynamicLeverage, isLoading }
}
